import { alert } from '@/services/alert';
import { reportError } from '@/services/error-reporting';
import { patientAccessNoteSearchParams } from '@bariendo/lib/patient-access-note-create';
import { TIMELINE_FOCUS_ITEM_QUERY_PARAM } from '@bariendo/lib/constants';
import { isNotFoundError } from '@bariendo/lib/error';
import { extractPatientsFromAppointment } from '@bariendo/lib/scheduling';
import { Anchor, Button, Flex, Loader } from '@mantine/core';
import { createReference, getReferenceString, isResource } from '@medplum/core';
import { Appointment, Communication, Patient, Practitioner, Reference } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { IconClipboardText } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { PatientAccessCardKind } from './patientAccessCardKind';

/**
 * The Chart row of an appointment a Patient Access rep works.
 *
 * Replaces the generic "Create a new chart note" link on an appointment that has
 * a PAC call script, and is the *only* way that card comes into existence. Nothing
 * creates it on booking: an empty card sitting on the timeline before the call
 * invites a second, parallel chart note from the generic link, drifts away from
 * its appointment when that appointment is rescheduled, and is left behind with
 * nothing in it if the call never happens.
 *
 * One click, no template picker — unlike an Encounter chart note there is
 * nothing to choose, since the appointment type already determines the script.
 */

function singlePatientReference(appointment: Appointment): Reference<Patient> | undefined {
  const patients = extractPatientsFromAppointment(appointment);
  if (patients.length !== 1) return undefined;

  const patient = patients[0];
  return isResource(patient, 'Patient') ? createReference(patient) : (patient as Reference<Patient>);
}

interface PatientAccessChartLinkProps {
  appointment: Appointment;
  kind: PatientAccessCardKind;
}

export function PatientAccessChartLink({ appointment, kind }: PatientAccessChartLinkProps) {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const [searchParams, setSearchParams] = useSearchParams();

  const [note, setNote] = useState<Communication | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  const appointmentRef = appointment.id ? getReferenceString(appointment) : undefined;
  const patient = useMemo(() => singlePatientReference(appointment), [appointment]);

  useEffect(() => {
    // No single patient means nothing to chart the call against, so there is no
    // card to look for either.
    if (!appointmentRef || !patient) {
      setIsLoading(false);
      return;
    }

    let isCancelled = false;
    const reference = appointmentRef;

    async function findExistingNote(): Promise<void> {
      try {
        const found = await medplum.searchOne(
          'Communication',
          patientAccessNoteSearchParams(reference, kind.categoryCode)
        );
        if (!isCancelled) {
          setNote(found ?? null);
        }
      } catch (error) {
        if (!isCancelled && !isNotFoundError(error)) {
          reportError(error, { component: 'PatientAccessChartLink', extra: { appointment: appointmentRef } });
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    void findExistingNote();

    return () => {
      isCancelled = true;
    };
  }, [appointmentRef, kind.categoryCode, medplum, patient]);

  // Scrolls the timeline to the card and highlights it. The card reads the same
  // param to open itself, so the rep lands on the script rather than on a
  // collapsed "Not started" line.
  const focusOnCard = useCallback(
    (target: Communication) => {
      const reference = getReferenceString(target);
      if (!reference) return;

      const nextParams = new URLSearchParams(searchParams);
      nextParams.set(TIMELINE_FOCUS_ITEM_QUERY_PARAM, reference);
      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams]
  );

  const startCard = async (): Promise<void> => {
    if (isCreating || !patient || !appointment.id) return;

    try {
      setIsCreating(true);
      const created = await kind.create(medplum, {
        patient,
        basedOn: createReference(appointment),
        // The PAC access policy scopes note writes to sender=%profile, so the rep
        // who starts the card is the one who can fill it in.
        ...(profile ? { sender: createReference(profile) } : {}),
        sent: appointment.start ?? new Date().toISOString(),
      });

      setNote(created);
      focusOnCard(created);
    } catch (error) {
      alert.error(`Couldn't start the call notes: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsCreating(false);
    }
  };

  if (!appointmentRef || !patient) {
    return null;
  }

  if (isLoading) {
    return <Loader size="xs" />;
  }

  if (note) {
    return (
      <Flex gap="xs" align="center">
        <IconClipboardText size={16} />
        <Anchor component="button" type="button" onClick={() => focusOnCard(note)}>
          View call notes
        </Anchor>
      </Flex>
    );
  }

  // Call notes already started stay reachable, but a cancelled call is not one
  // to start.
  if (appointment.status === 'cancelled') {
    return null;
  }

  return (
    <Button
      variant="light"
      size="compact-sm"
      leftSection={<IconClipboardText size={16} />}
      loading={isCreating}
      onClick={startCard}
    >
      {kind.startLabel}
    </Button>
  );
}
